import React, { useState, useEffect } from 'react'
import { useEditorSettings, type CompilerType, type EditorTheme, type OverallTheme, type KeybindingType } from '@stores/editorSettingsStore'
import { aiProvidersAPI } from '@api/aiProviders'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import KeyboardShortcutsModal from './KeyboardShortcutsModal'
import toast from 'react-hot-toast'

const EditorSettingsPanel: React.FC = () => {
  const { settings, updateSettings } = useEditorSettings()
  const [showShortcuts, setShowShortcuts] = useState(false)

  const compilerOptions: { value: CompilerType; label: string }[] = [
    { value: 'pdfLaTeX', label: 'pdfLaTeX' },
    { value: 'LaTeX', label: 'LaTeX' },
    { value: 'XeLaTeX', label: 'XeLaTeX' },
    { value: 'LuaLaTeX', label: 'LuaLaTeX' },
  ]

  const editorThemeOptions: { value: EditorTheme; label: string }[] = [
    { value: 'scidraft', label: 'SciDraft Classic' },
    { value: 'default', label: 'Default' },
    { value: 'monokai', label: 'Monokai' },
    { value: 'solarized', label: 'Solarized' },
  ]

  const overallThemeOptions: { value: OverallTheme; label: string }[] = [
    { value: 'light', label: 'Light' },
    { value: 'dark', label: 'Dark' },
    { value: 'auto', label: 'Auto (System)' },
  ]

  const keybindingOptions: { value: KeybindingType; label: string }[] = [
    { value: 'default', label: 'Default' },
    { value: 'vim', label: 'Vim' },
    { value: 'emacs', label: 'Emacs' },
    { value: 'sublime', label: 'Sublime Text' },
    { value: 'vscode', label: 'VS Code' },
  ]

  const fontFamilyOptions = [
    { value: 'Lucida Console, Monaco, "Courier New", monospace', label: 'Lucida / Source Code' },
    { value: 'Monaco, "Courier New", monospace', label: 'Monaco' },
    { value: '"Courier New", Courier, monospace', label: 'Courier New' },
    { value: 'Consolas, "Courier New", monospace', label: 'Consolas' },
    { value: '"Fira Code", "Courier New", monospace', label: 'Fira Code' },
    { value: '"JetBrains Mono", "Courier New", monospace', label: 'JetBrains Mono' },
  ]

  const lineHeightOptions = [
    { value: 'normal', label: 'Normal' },
    { value: 'compact', label: 'Compact' },
    { value: 'comfortable', label: 'Comfortable' },
  ]

  const spellCheckLanguages = [
    { value: 'en-US', label: 'English (American)' },
    { value: 'en-GB', label: 'English (British)' },
    { value: 'es-ES', label: 'Spanish' },
    { value: 'fr-FR', label: 'French' },
    { value: 'de-DE', label: 'German' },
    { value: 'it-IT', label: 'Italian' },
    { value: 'pt-BR', label: 'Portuguese (Brazilian)' },
    { value: 'zh-CN', label: 'Chinese (Simplified)' },
    { value: 'ja-JP', label: 'Japanese' },
  ]

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Editor Settings
        </h2>

        {/* Compiler */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Compiler
          </label>
          <select
            value={settings.compiler}
            onChange={(e) => handleCompilerChange(e.target.value as CompilerType)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            {compilerOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* Editor Theme */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Editor Theme
          </label>
          <select
            value={settings.editorTheme}
            onChange={(e) => updateSettings({ editorTheme: e.target.value as EditorTheme })}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            {editorThemeOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* Overall Theme */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Overall Theme
          </label>
          <select
            value={settings.overallTheme}
            onChange={(e) => updateSettings({ overallTheme: e.target.value as OverallTheme })}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            {overallThemeOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* Font Size */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Font Size: {settings.fontSize}px
          </label>
          <input
            type="range"
            min="10"
            max="24"
            value={settings.fontSize}
            onChange={(e) => updateSettings({ fontSize: parseInt(e.target.value) })}
            className="w-full"
          />
          <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
            <span>10px</span>
            <span>24px</span>
          </div>
        </div>

        {/* Font Family */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Font Family
          </label>
          <select
            value={settings.fontFamily}
            onChange={(e) => updateSettings({ fontFamily: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            {fontFamilyOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* Line Height */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Line Height
          </label>
          <select
            value={typeof settings.lineHeight === 'string' ? settings.lineHeight : 'normal'}
            onChange={(e) => {
              const value = e.target.value
              updateSettings({ 
                lineHeight: value === 'normal' || value === 'compact' || value === 'comfortable' 
                  ? value 
                  : 'normal' 
              })
            }}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            {lineHeightOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* Keybindings */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Keybindings
          </label>
          <select
            value={settings.keybindings}
            onChange={(e) => updateSettings({ keybindings: e.target.value as KeybindingType })}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            {keybindingOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* Spell Check */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Spell Check
          </label>
          <select
            value={settings.spellCheck}
            onChange={(e) => updateSettings({ spellCheck: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            {spellCheckLanguages.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* Editor Behavior Toggles */}
        <div className="space-y-3 mb-4">
          <div className="flex items-center justify-between">
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Auto-complete
              </label>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Show autocomplete suggestions as you type
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={settings.autoComplete}
                onChange={(e) => updateSettings({ autoComplete: e.target.checked })}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-primary-600"></div>
            </label>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Auto-close brackets
              </label>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Automatically close brackets, braces, and quotes
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={settings.autoCloseBrackets}
                onChange={(e) => updateSettings({ autoCloseBrackets: e.target.checked })}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-primary-600"></div>
            </label>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Code check
              </label>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Show syntax errors and warnings
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={settings.codeCheck}
                onChange={(e) => updateSettings({ codeCheck: e.target.checked })}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-primary-600"></div>
            </label>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Equation preview
              </label>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Show preview of LaTeX equations
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={settings.equationPreview}
                onChange={(e) => updateSettings({ equationPreview: e.target.checked })}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-primary-600"></div>
            </label>
          </div>
        </div>

        {/* Keyboard Shortcuts */}
        <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={() => setShowShortcuts(true)}
            className="text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
          >
            Show Hotkeys
          </button>
        </div>
      </div>

      <KeyboardShortcutsModal
        isOpen={showShortcuts}
        onClose={() => setShowShortcuts(false)}
      />
    </div>
  )
}

export default EditorSettingsPanel

